import { useQuery } from '@tanstack/react-query';
import { subscriptionService } from '../services/subscriptionService';
import type {
  Product,
  AvailablePlansResponse,
  CurrentSubscription
} from '../types/subscription.types';

/**
 * Hook to get available subscription plans
 */
export function useAvailablePlans() {
  return useQuery<AvailablePlansResponse>({
    queryKey: ['subscription', 'available-plans'],
    queryFn: () => subscriptionService.getAvailablePlans(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to get product information (all plans)
 */
export function useProductInfo() {
  return useQuery<Product>({
    queryKey: ['subscription', 'product-info'],
    queryFn: async () => {
      const response = await subscriptionService.getProductInfo();
      return response.product;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
}

/**
 * Hook to get current subscription
 */
export function useCurrentSubscription() {
  return useQuery<CurrentSubscription>({
    queryKey: ['subscription', 'current'],
    queryFn: () => subscriptionService.getCurrentSubscription(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Main hook that combines available plans and current subscription
 * This gives you everything you need about the user's subscription
 */
export function useSubscription() {
  const availablePlansQuery = useAvailablePlans();
  const currentSubscriptionQuery = useCurrentSubscription();

  // Get the current plan from available plans
  const currentPlan = availablePlansQuery.data?.available_plans?.find(
    plan => plan.is_current
  );

  // Get plan limits
  const planLimits = currentPlan?.limits;

  return {
    // Available plans data
    availablePlans: availablePlansQuery.data?.available_plans || [],
    productInfo: availablePlansQuery.data?.product,
    hasActiveSubscription: availablePlansQuery.data?.has_active_subscription || false,

    // Current subscription
    currentSubscription: availablePlansQuery.data?.current_subscription,
    currentPlan,
    planLimits,

    // Loading states
    isLoading: availablePlansQuery.isLoading || currentSubscriptionQuery.isLoading,
    isError: availablePlansQuery.isError || currentSubscriptionQuery.isError,
    error: availablePlansQuery.error || currentSubscriptionQuery.error,

    // Refetch function
    refetch: () => {
      availablePlansQuery.refetch();
      currentSubscriptionQuery.refetch();
    },
  };
}
